import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery,
  TablePagination,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Send as SendIcon,
  FilterAltOff,
  NotificationsActive as NotificationsActiveIcon,
  CloseRounded,
  Campaign,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useLanguage } from '../context/LanguageContext';
import { useNotifications } from '../context/NotificationContext';
import { customPushNotificationsAPI, categoryPushNotificationsAPI, contactsAPI, promotionsAPI } from '../services/api';
import { toast } from 'react-toastify';

const ACCENT_COLOR = '#FF8C42';
const STORAGE_KEY = 'getsettled_custom_push_notifications_v4';

const extractListFromResponse = (res) => {
  if (!res) return [];
  const body = res.data !== undefined ? res.data : res;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body?.payload)) return body.payload;
  if (Array.isArray(body?.result)) return body.result;
  if (Array.isArray(body?.data?.items)) return body.data.items;
  if (Array.isArray(body?.data?.payload)) return body.data.payload;
  if (body?.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
    return [body.data];
  }
  return [];
};

const extractHistoryFromResponse = (res) => {
  if (!res) return { items: [], total: 0 };
  const body = res.data !== undefined ? res.data : res;
  let items = [];
  let total = 0;

  if (Array.isArray(body)) {
    items = body;
    total = body.length;
  } else if (Array.isArray(body?.payload)) {
    items = body.payload;
    total = body.totalRecord ?? body.total ?? body.payload.length;
  } else if (Array.isArray(body?.data)) {
    items = body.data;
    total = body.totalRecord ?? body.total ?? body.data.length;
  } else if (Array.isArray(body?.items)) {
    items = body.items;
    total = body.totalRecord ?? body.total ?? body.items.length;
  } else if (body?.data && typeof body.data === 'object') {
    if (Array.isArray(body.data.items)) {
      items = body.data.items;
      total = body.data.totalRecord ?? body.data.total ?? body.data.items.length;
    } else if (Array.isArray(body.data.payload)) {
      items = body.data.payload;
      total = body.data.totalRecord ?? body.data.total ?? body.data.payload.length;
    }
  }

  return { items, total };
};

const normalizeCustomHistoryItem = (item) => {
  if (!item) return null;
  return {
    id: item.id !== undefined && item.id !== null ? item.id : (item.Id !== undefined ? item.Id : `cat-${Date.now()}`),
    module: item.module || item.Module || 'Contact',
    entityId: item.entityId !== undefined ? item.entityId : (item.EntityId !== undefined ? item.EntityId : null),
    categoryId: item.categoryId !== undefined ? item.categoryId : (item.CategoryId !== undefined ? item.CategoryId : null),
    cityId: item.cityId !== undefined ? item.cityId : (item.CityId !== undefined ? item.CityId : null),
    title: item.title || item.Title || '',
    body: item.body || item.message || item.Body || item.Message || '',
    lookbackDays: item.lookbackDays !== undefined ? item.lookbackDays : (item.LookbackDays !== undefined ? item.LookbackDays : 30),
    status: item.status || item.Status || 'Pending',
    adminNote: item.adminNote || item.AdminNote || item.note || item.Note || '',
    totalEligibleRecipients: item.totalEligibleRecipients ?? item.TotalEligibleRecipients ?? 0,
    successCount: item.successCount ?? item.SuccessCount ?? 0,
    failureCount: item.failureCount ?? item.FailureCount ?? 0,
    sentAtUtc: item.sentAtUtc || item.SentAtUtc || null,
    createdAtUtc: item.createdAtUtc || item.CreatedAtUtc || item.createdAt || item.CreatedAt || new Date().toISOString(),
    senderRole: item.senderRole || item.SenderRole || '',
    senderName: item.senderName || item.SenderName || '',
    senderEmail: item.senderEmail || item.SenderEmail || '',
  };
};

const extractItemFromResponse = (res) => {
  if (!res) return null;
  const body = res.data !== undefined ? res.data : res;
  if (body?.data && typeof body.data === 'object' && !Array.isArray(body.data)) return body.data;
  if (body && typeof body === 'object' && !Array.isArray(body)) return body;
  return null;
};

const normalizeNotification = (item) => {
  if (!item) return null;
  const notifTitle = item.title || item.Title || '';
  const notifBody = item.message || item.body || item.Message || item.Body || '';
  const notifStatus = item.status || item.Status || 'Pending';
  const notifNote = item.adminNote || item.AdminNote || item.note || item.Note || '';
  const notifCreatedAt = item.createdAtUtc || item.CreatedAtUtc || item.createdAt || item.CreatedAt || new Date().toISOString();
  const notifId = item.id !== undefined && item.id !== null ? item.id : (item.Id !== undefined ? item.Id : `push-${Date.now()}`);
  
  const notifTargetType = item.targetType || item.TargetType || item.category || item.Category || 'Custom';
  const notifTargetEntityId = item.targetEntityId !== undefined && item.targetEntityId !== null
    ? item.targetEntityId
    : (item.TargetEntityId !== undefined && item.TargetEntityId !== null ? item.TargetEntityId : null);

  const notifContactId = item.contactId !== undefined && item.contactId !== null
    ? item.contactId
    : (notifTargetType === 'Contact' ? notifTargetEntityId : '');
  const notifContactTitle = item.contactTitle || item.ContactTitle || item.contact?.title || item.Contact?.Title || item.contactName || item.ContactName || item.contact?.name || item.Contact?.Name || '';

  const notifPromotionId = item.promotionId !== undefined && item.promotionId !== null
    ? item.promotionId
    : (notifTargetType === 'Promotion' ? notifTargetEntityId : '');
  const notifPromotionTitle = item.promotionTitle || item.PromotionTitle || item.promotion?.title || item.Promotion?.Title || item.promotionName || item.PromotionName || '';

  const notifTargetTitle = item.targetTitle || item.TargetTitle || item.targetName || item.TargetName || notifContactTitle || notifPromotionTitle || '';

  return {
    id: notifId,
    title: notifTitle,
    body: notifBody,
    message: notifBody,
    status: notifStatus,
    note: notifNote,
    adminNote: notifNote,
    createdAt: notifCreatedAt,
    createdAtUtc: notifCreatedAt,
    targetType: notifTargetType,
    targetEntityId: notifTargetEntityId,
    targetTitle: notifTargetTitle,
    category: notifTargetType,
    contactId: notifContactId,
    contactTitle: notifContactTitle,
    promotionId: notifPromotionId,
    promotionTitle: notifPromotionTitle,
    providerUserId: item.providerUserId || item.ProviderUserId,
    providerName: item.providerName || item.ProviderName || '',
    providerEmail: item.providerEmail || item.ProviderEmail || '',
  };
};

export default function CustomPushNotifications() {
  const { t, locale } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { refresh: refreshNotifications } = useNotifications();

  // API & Local State Management
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [notifications, setNotifications] = useState([]);

  // Contacts & Promotions State
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [loadingPromotions, setLoadingPromotions] = useState(false);

  // Clear legacy mock items cached in localStorage on mount
  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem('getsettled_custom_push_notifications_v4');
      localStorage.removeItem('getsettled_custom_push_notifications_v3');
    } catch (e) {
      // Ignore
    }
  }, []);

  // GET /api/customer/custom-push-notifications — Fetch list on mount
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await customPushNotificationsAPI.getNotifications();
      const rawList = extractListFromResponse(res);
      const normalized = rawList.map(normalizeNotification).filter(Boolean);
      setNotifications(normalized);
    } catch (e) {
      console.error('Failed to fetch notifications from API:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // GET /api/customer/contacts — Fetch contact list for dropdown selection
  const fetchContacts = useCallback(async () => {
    setLoadingContacts(true);
    try {
      const res = await contactsAPI.getContacts('', '', 0, 100);
      const data = res?.data;
      const list = data?.data ?? data?.payload ?? (Array.isArray(data) ? data : []);
      setContacts(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Failed to fetch contacts from API:', e);
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  // GET /api/customer/promotions — Fetch promotions list for dropdown selection
  const fetchPromotions = useCallback(async () => {
    setLoadingPromotions(true);
    try {
      const res = await promotionsAPI.getPromotions({ pageSize: 100 });
      const data = res?.data;
      const list = data?.data ?? data?.payload ?? (Array.isArray(data) ? data : []);
      setPromotions(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Failed to fetch promotions from API:', e);
    } finally {
      setLoadingPromotions(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchContacts();
    fetchPromotions();
  }, [fetchNotifications, fetchContacts, fetchPromotions]);

  // Notification Type Tab State: 'generic' | 'custom'
  const [activeTab, setActiveTab] = useState('generic');

  // Custom Category Push Notification State
  const [customHistory, setCustomHistory] = useState([]);
  const [customLoading, setCustomLoading] = useState(false);
  const [customTotalRecords, setCustomTotalRecords] = useState(0);
  const [customPage, setCustomPage] = useState(1);
  const [customPageSize, setCustomPageSize] = useState(20);

  // Custom Notification Modal State
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customSending, setCustomSending] = useState(false);
  const [customCategory, setCustomCategory] = useState('Contact');
  const [customContactId, setCustomContactId] = useState('');
  const [customPromotionId, setCustomPromotionId] = useState('');
  const [customTitle, setCustomTitle] = useState('');
  const [customBody, setCustomBody] = useState('');

  // Custom View Modal State
  const [customViewItem, setCustomViewItem] = useState(null);

  // GET /api/customer/category-push-notifications/history?page=1&pageSize=20
  const fetchCustomHistory = useCallback(async () => {
    setCustomLoading(true);
    try {
      const res = await categoryPushNotificationsAPI.getHistory(customPage, customPageSize);
      const { items, total } = extractHistoryFromResponse(res);
      const normalized = items.map(normalizeCustomHistoryItem).filter(Boolean);
      setCustomHistory(normalized);
      setCustomTotalRecords(total || normalized.length);
    } catch (e) {
      console.error('Failed to fetch category push notifications history:', e);
      setCustomHistory([]);
      setCustomTotalRecords(0);
    } finally {
      setCustomLoading(false);
    }
  }, [customPage, customPageSize]);

  useEffect(() => {
    fetchCustomHistory();
  }, [fetchCustomHistory]);

  const handleOpenCustomAdd = () => {
    setCustomCategory('Contact');
    setCustomContactId('');
    setCustomPromotionId('');
    setCustomTitle('');
    setCustomBody('');
    setCustomDialogOpen(true);
  };

  const handleSendCustomNotification = async () => {
    if (customCategory === 'Contact' && !customContactId) {
      toast.warn(t('pages.customPush.toastSelectContact'), { position: 'top-center' });
      return;
    }
    if (customCategory === 'Promotion' && !customPromotionId) {
      toast.warn(t('pages.customPush.toastSelectPromotion'), { position: 'top-center' });
      return;
    }
    if (!customTitle.trim()) {
      toast.warn(t('pages.customPush.toastEnterTitle'), { position: 'top-center' });
      return;
    }
    if (!customBody.trim()) {
      toast.warn(t('pages.customPush.toastEnterMessage'), { position: 'top-center' });
      return;
    }

    setCustomSending(true);
    try {
      const selectedEntityId = customCategory === 'Contact' ? customContactId : customPromotionId;
      const payload = {
        module: customCategory || 'Contact',
        entityId: Number(selectedEntityId),
        title: customTitle.trim(),
        body: customBody.trim(),
        lookbackDays: 30,
      };

      await categoryPushNotificationsAPI.sendNotification(payload);
      toast.success(t('pages.customPush.toastCustomSentSuccess'), { position: 'top-center' });
      setCustomDialogOpen(false);
      setCustomCategory('Contact');
      setCustomContactId('');
      setCustomPromotionId('');
      setCustomTitle('');
      setCustomBody('');
      fetchCustomHistory();
      refreshNotifications?.();
    } catch (err) {
      console.error('Failed to send category push notification:', err);
      toast.error(err?.response?.data?.message || err?.message || t('pages.customPush.toastCustomSendFailed'), { position: 'top-center' });
    } finally {
      setCustomSending(false);
    }
  };

  // Unified Search & Filters for both Generic & Custom
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [serviceFilter, setServiceFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  // Modal / Form States
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);

  // View Modal State
  const [viewItem, setViewItem] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Form Fields
  const [category, setCategory] = useState('Contact');
  const [contactId, setContactId] = useState('');
  const [promotionId, setPromotionId] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  // GET /api/customer/custom-push-notifications/{id} — Fetch detail on view icon click
  const handleOpenView = async (item) => {
    setViewItem(item);
    setViewLoading(true);
    try {
      const res = await customPushNotificationsAPI.getNotificationById(item.id);
      const fetchedData = extractItemFromResponse(res);
      if (fetchedData) {
        const normalized = normalizeNotification(fetchedData);
        setViewItem({
          ...normalized,
          ...fetchedData,
        });
      }
    } catch (err) {
      console.error('Error fetching notification detail for view:', err);
    } finally {
      setViewLoading(false);
    }
  };

  // Open Dialog for Add or Edit
  const handleOpenAdd = () => {
    setEditingItem(null);
    setCategory('Contact');
    setContactId('');
    setPromotionId('');
    setTitle('');
    setBody('');
    setDialogOpen(true);
  };

  // GET /api/customer/custom-push-notifications/{id} — Fetch detail on edit
  const handleOpenEdit = async (item) => {
    if (item.status === 'Approved') {
      toast.error(t('pages.customPush.toastApprovedCannotEdit'));
      return;
    }
    setEditingItem(item);
    const initialCategory = item.category || (item.contactId ? 'Contact' : item.promotionId ? 'Promotion' : 'Custom');
    setCategory(initialCategory);
    setContactId(item.contactId || '');
    setPromotionId(item.promotionId || '');
    setTitle(item.title || '');
    setBody(item.body || item.message || '');
    setDialogOpen(true);

    try {
      const res = await customPushNotificationsAPI.getNotificationById(item.id);
      const fetchedData = extractItemFromResponse(res);
      if (fetchedData) {
        const normalized = normalizeNotification(fetchedData);
        setCategory(normalized.category || initialCategory);
        setContactId(normalized.contactId || item.contactId || '');
        setPromotionId(normalized.promotionId || item.promotionId || '');
        setTitle(normalized.title || item.title || '');
        setBody(normalized.body || item.body || '');
      }
    } catch (err) {
      console.error('Error fetching notification detail from API:', err);
    }
  };

  // Save / Send Form Action — POST Create or PUT Update
  const handleSaveForm = async () => {
    if (category === 'Contact' && !contactId) {
      toast.warn(t('pages.customPush.toastSelectContact'), { position: 'top-center' });
      return;
    }
    if (category === 'Promotion' && !promotionId) {
      toast.warn(t('pages.customPush.toastSelectPromotion'), { position: 'top-center' });
      return;
    }
    if (!title.trim()) {
      toast.warn(t('pages.customPush.toastEnterTitle'), { position: 'top-center' });
      return;
    }
    if (!body.trim()) {
      toast.warn(t('pages.customPush.toastEnterMessage'), { position: 'top-center' });
      return;
    }

    setSubmitting(true);
    try {
      let targetEntityId = null;
      if (category === 'Contact' && contactId) {
        targetEntityId = typeof contactId === 'number' ? contactId : (parseInt(contactId, 10) || null);
      } else if (category === 'Promotion' && promotionId) {
        targetEntityId = typeof promotionId === 'number' ? promotionId : (parseInt(promotionId, 10) || null);
      } else {
        targetEntityId = null;
      }

      const selectedContactObj = contacts.find((c) => String(c.id) === String(contactId));
      const contactTitle = selectedContactObj?.title || selectedContactObj?.titleDe || selectedContactObj?.name || '';

      const selectedPromoObj = promotions.find((p) => String(p.id) === String(promotionId));
      const promotionTitle = selectedPromoObj?.title || selectedPromoObj?.name || '';

      const payload = {
        title: title.trim(),
        message: body.trim(),
        targetType: category,
        targetEntityId,
        category,
        contactId: category === 'Contact' ? targetEntityId : null,
        promotionId: category === 'Promotion' ? targetEntityId : null,
      };

      if (editingItem) {
        if (editingItem.status === 'Approved') {
          toast.error(t('pages.customPush.toastApprovedCannotEdit'), { position: 'top-center' });
          return;
        }
        // PUT /api/customer/custom-push-notifications/{id}
        const res = await customPushNotificationsAPI.updateNotification(editingItem.id, payload);
        const resData = extractItemFromResponse(res);
        const updatedItem = normalizeNotification(resData) || {
          ...editingItem,
          title: title.trim(),
          body: body.trim(),
          message: body.trim(),
          targetType: category,
          targetEntityId,
          category,
          contactId: category === 'Contact' ? targetEntityId : null,
          contactTitle,
          promotionId: category === 'Promotion' ? targetEntityId : null,
          promotionTitle,
        };

        setNotifications((prev) => prev.map((n) => (n.id === editingItem.id ? updatedItem : n)));
        toast.success(t('pages.customPush.toastUpdatedSuccess'), { position: 'top-center' });
      } else {
        // POST /api/customer/custom-push-notifications
        const res = await customPushNotificationsAPI.createNotification(payload);
        const resData = extractItemFromResponse(res);
        const newItem = normalizeNotification(resData) || {
          id: `push-${Date.now()}`,
          title: title.trim(),
          body: body.trim(),
          message: body.trim(),
          targetType: category,
          targetEntityId,
          category,
          contactId: category === 'Contact' ? targetEntityId : null,
          contactTitle,
          promotionId: category === 'Promotion' ? targetEntityId : null,
          promotionTitle,
          note: 'Created by provider',
          status: 'Pending',
          createdAt: new Date().toISOString(),
        };

        setNotifications((prev) => [newItem, ...prev]);
        toast.success(t('pages.customPush.toastCreatedSuccess'), { position: 'top-center' });
      }
      refreshNotifications?.();
      setDialogOpen(false);
    } catch (err) {
      console.error('Failed to save custom push notification:', err);
      toast.error(err?.response?.data?.message || t('pages.customPush.toastSaveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Item — DELETE /api/customer/custom-push-notifications/{id}
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmItem) return;
    if (deleteConfirmItem.status === 'Approved') {
      toast.error(t('pages.customPush.toastApprovedCannotDelete'));
      setDeleteConfirmItem(null);
      return;
    }

    setDeleting(true);
    try {
      await customPushNotificationsAPI.deleteNotification(deleteConfirmItem.id);
      setNotifications((prev) => prev.filter((n) => n.id !== deleteConfirmItem.id));
      toast.success(t('pages.customPush.toastDeletedSuccess'));
    } catch (err) {
      console.error('Failed to delete notification:', err);
      toast.error(err?.response?.data?.message || t('pages.customPush.toastDeleteFailed'));
    } finally {
      setDeleting(false);
      setDeleteConfirmItem(null);
    }
  };

  // Filtered Notifications
  const filteredNotifications = useMemo(() => {
    return notifications.filter((item) => {
      const matchesSearch =
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.body.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.note && item.note.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (item.targetTitle && item.targetTitle.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesStatus = statusFilter === 'All' || item.status.toLowerCase() === statusFilter.toLowerCase();
      const itemCategory = item.targetType || item.category || 'Custom';
      const matchesCategory = categoryFilter === 'All' || itemCategory === categoryFilter;
      const itemContactId = item.contactId || (itemCategory === 'Contact' ? item.targetEntityId : null);
      const matchesService = serviceFilter === 'All' || String(itemContactId) === String(serviceFilter);
      return matchesSearch && matchesStatus && matchesCategory && matchesService;
    });
  }, [notifications, searchQuery, statusFilter, categoryFilter, serviceFilter]);

  // Filtered Custom Category History
  const filteredCustomHistory = useMemo(() => {
    return customHistory.filter((item) => {
      const isPromo = item.module === 'Promotion';
      const matchedEntity = isPromo
        ? promotions.find((p) => String(p.id) === String(item.entityId))
        : contacts.find((c) => String(c.id) === String(item.entityId));
      const entityTitle = matchedEntity?.title || matchedEntity?.titleDe || matchedEntity?.name || '';
      const matchesSearch =
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.body.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entityTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.adminNote && item.adminNote.toLowerCase().includes(searchQuery.toLowerCase()));
      const itemStatusLower = String(item.status || '').toLowerCase();
      const filterLower = statusFilter.toLowerCase();
      const matchesStatus =
        statusFilter === 'All' ||
        itemStatusLower === filterLower ||
        (filterLower === 'approved' && itemStatusLower === 'sent');
      const matchesCategory = categoryFilter === 'All' || item.module === categoryFilter;
      const matchesService = serviceFilter === 'All' || (item.module === 'Contact' && String(item.entityId) === String(serviceFilter));
      return matchesSearch && matchesStatus && matchesCategory && matchesService;
    });
  }, [customHistory, searchQuery, statusFilter, categoryFilter, serviceFilter, contacts, promotions]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const loc = locale === 'de' ? 'de-DE' : 'en-US';
      return new Date(iso).toLocaleString(loc, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return String(iso);
    }
  };

  return (
    <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, pr: { xs: 1, sm: 2 } }}>
      {/* Header Section — Matching Contacts.js */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
          p: { xs: 2, sm: 4 },
          borderRadius: { xs: 2, sm: 4 },
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
          border: '1px solid rgba(255, 140, 66, 0.2)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        }}
      >
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
          <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 800, letterSpacing: '-0.03em', color: '#37373a' }}>
            {t('pages.customPush.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 500 }}>
            {t('pages.customPush.subtitle')}
          </Typography>
        </Box>
        {/* Right side controls: Notification Type dropdown & Add button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 230 }, bgcolor: '#FFFFFF' }}>
            <Select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              sx={{
                borderRadius: '12px',
                fontWeight: 700,
                fontSize: '0.925rem',
                color: '#0F172A',
                height: '48px',
                bgcolor: '#FFFFFF',
                '& fieldset': { borderColor: '#E2E8F0', borderWidth: '1.5px' },
                '&:hover fieldset': { borderColor: ACCENT_COLOR },
                '&.Mui-focused fieldset': { borderColor: ACCENT_COLOR },
              }}
            >
              <MenuItem value="generic" sx={{ fontWeight: 600, py: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
                  <NotificationsActiveIcon sx={{ color: activeTab === 'generic' ? ACCENT_COLOR : '#64748B', fontSize: 20 }} />
                  <span>{t('pages.customPush.tabGeneric')}</span>
                </Box>
              </MenuItem>
              <MenuItem value="custom" sx={{ fontWeight: 600, py: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
                  <Campaign sx={{ color: activeTab === 'custom' ? ACCENT_COLOR : '#64748B', fontSize: 20 }} />
                  <span>{t('pages.customPush.tabCustom')}</span>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            fullWidth={isMobile}
            startIcon={<Add />}
            onClick={activeTab === 'generic' ? handleOpenAdd : handleOpenCustomAdd}
            sx={{
              px: 3.5,
              py: 1.4,
              height: '48px',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              background: ACCENT_COLOR,
              boxShadow: '0 4px 15px rgba(255, 140, 66, 0.3)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: '#E06526',
                transform: 'translateY(-2px) scale(1.02)',
                boxShadow: '0 8px 25px rgba(19, 88, 163, 0.4)',
              },
              '&:active': { transform: 'translateY(0)' },
            }}
          >
            {activeTab === 'generic' ? t('pages.customPush.addNotification') : t('pages.customPush.sendCustomNotification')}
          </Button>
        </Box>
      </Box>

      {/* Unified Filter Section for both Generic and Custom */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            placeholder={t('pages.customPush.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              sx: { borderRadius: '12px', bgcolor: 'white' },
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.5}>
          <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}>
            <InputLabel>{t('pages.customPush.fieldCategory')}</InputLabel>
            <Select
              value={categoryFilter}
              label={t('pages.customPush.fieldCategory')}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="All">{t('pages.customPush.allCategories')}</MenuItem>
              <MenuItem value="Contact">{t('pages.customPush.categoryContact')}</MenuItem>
              <MenuItem value="Promotion">{t('pages.customPush.categoryPromotion')}</MenuItem>
              {activeTab === 'generic' && <MenuItem value="Custom">{t('pages.customPush.categoryCustom')}</MenuItem>}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2.5}>
          <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}>
            <InputLabel>{t('pages.customPush.filterService') || t('pages.customPush.fieldContact')}</InputLabel>
            <Select
              value={serviceFilter}
              label={t('pages.customPush.filterService') || t('pages.customPush.fieldContact')}
              onChange={(e) => setServiceFilter(e.target.value)}
              disabled={categoryFilter === 'Promotion' || categoryFilter === 'Custom'}
            >
              <MenuItem value="All">{t('pages.customPush.allServices') || 'All Services'}</MenuItem>
              {contacts.map((c) => {
                const label = c.title || c.titleDe || c.name || t('pages.customPush.categoryContact');
                return (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white' } }}>
            <InputLabel>{t('pages.customPush.status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('pages.customPush.status')}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="All">{t('pages.customPush.allStatuses')}</MenuItem>
              <MenuItem value="Approved">{t('pages.customPush.statusApproved')}</MenuItem>
              <MenuItem value="Pending">{t('pages.customPush.statusPending')}</MenuItem>
              <MenuItem value="Rejected">{t('pages.customPush.statusRejected')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<FilterAltOff />}
            onClick={() => {
              setSearchQuery('');
              setCategoryFilter('All');
              setServiceFilter('All');
              setStatusFilter('All');
            }}
            sx={{
              height: '56px',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              borderWidth: '2px',
              '&:hover': { borderWidth: '2px' },
            }}
          >
            {t('pages.customPush.clearFilters')}
          </Button>
        </Grid>
      </Grid>

      {/* ── GENERIC NOTIFICATIONS VIEW ── */}
      {activeTab === 'generic' && (
        <>

          {/* Notifications Table Layout */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #E2E8F0',
              overflow: 'hidden',
              bgcolor: '#ffffff',
              boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)',
            }}
          >
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderDetails')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderCategory')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderTargetTitle')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderNote')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderDate')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderStatus')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} sx={{ color: ACCENT_COLOR }} />
                        <Typography variant="body2" sx={{ mt: 1.5, color: '#64748B', fontWeight: 500 }}>
                          {t('pages.customPush.loading')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredNotifications.length > 0 ? (
                    filteredNotifications.map((item) => (
                      <TableRow key={item.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: '10px',
                                bgcolor: '#E6F2F5',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                mt: 0.25,
                              }}
                            >
                              <NotificationsActiveIcon sx={{ color: '#4A7C8E', fontSize: 20 }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0F172A', mb: 0.25 }}>
                                {item.title}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#64748B',
                                  fontSize: '0.825rem',
                                  fontWeight: 400,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {item.body && item.body.length > 70 ? `${item.body.substring(0, 70)}...` : item.body}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {(() => {
                            const cat = item.targetType || item.category || 'Custom';
                            const isContact = cat === 'Contact';
                            const isPromo = cat === 'Promotion';
                            const labelKey = `pages.customPush.category${cat}`;
                            const displayLabel = t(labelKey) !== labelKey ? t(labelKey) : cat;

                            return (
                              <Chip
                                label={displayLabel}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.72rem',
                                  fontWeight: 600,
                                  borderRadius: '8px',
                                  bgcolor: isContact ? '#E0F2FE' : isPromo ? '#F3E8FF' : '#F1F5F9',
                                  color: isContact ? '#0369A1' : isPromo ? '#6B21A8' : '#334155',
                                  border: isContact ? '1px solid #BAE6FD' : isPromo ? '1px solid #E9D5FF' : '1px solid #E2E8F0',
                                }}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ py: 2, fontSize: '0.825rem', color: '#1E293B', fontWeight: 600, maxWidth: 200 }}>
                          {(() => {
                            const cat = item.targetType || item.category || 'Custom';
                            let targetTitleValue = item.targetTitle;
                            if (!targetTitleValue) {
                              if (cat === 'Contact' || item.contactId) {
                                const cId = item.contactId || item.targetEntityId;
                                const foundContact = contacts.find((c) => String(c.id) === String(cId));
                                targetTitleValue = foundContact?.title || foundContact?.titleDe || foundContact?.name || item.contactTitle || (cId ? t('pages.customPush.categoryContact') : '');
                              } else if (cat === 'Promotion' || item.promotionId) {
                                const pId = item.promotionId || item.targetEntityId;
                                const foundPromo = promotions.find((p) => String(p.id) === String(pId));
                                targetTitleValue = foundPromo?.title || foundPromo?.name || item.promotionTitle || (pId ? t('pages.customPush.categoryPromotion') : '');
                              }
                            }

                            if (!targetTitleValue || cat === 'Custom') {
                              return (
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#94A3B8', fontStyle: 'italic' }}>
                                  —
                                </Typography>
                              );
                            }

                            return (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: '#1E293B',
                                  fontSize: '0.825rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  maxWidth: 190,
                                }}
                                title={targetTitleValue}
                              >
                                {targetTitleValue}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ py: 2, fontSize: '0.825rem', color: '#475569', maxWidth: 220 }}>
                          {item.note ? (
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.4 }}>
                              {item.note}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#94A3B8', fontStyle: 'italic' }}>
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 2, fontSize: '0.85rem', color: '#64748B', fontWeight: 500 }}>
                          {formatDate(item.createdAt)}
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          {item.status === 'Approved' && (
                            <Chip
                              label={t('pages.customPush.statusApproved')}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                borderRadius: '8px',
                                bgcolor: '#D1FAE5',
                                color: '#065F46',
                                border: '1px solid #A7F3D0',
                              }}
                            />
                          )}
                          {item.status === 'Pending' && (
                            <Chip
                              label={t('pages.customPush.statusPending')}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                borderRadius: '8px',
                                bgcolor: '#FEF3C7',
                                color: '#92400E',
                                border: '1px solid #FDE68A',
                              }}
                            />
                          )}
                          {item.status === 'Rejected' && (
                            <Chip
                              label={t('pages.customPush.statusRejected')}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                borderRadius: '8px',
                                bgcolor: '#FEE2E2',
                                color: '#991B1B',
                                border: '1px solid #FCA5A5',
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
                            {/* View Button */}
                            <Tooltip title={t('pages.customPush.tooltipView')}>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenView(item)}
                                sx={{
                                  width: 34,
                                  height: 34,
                                  bgcolor: '#F1F5F9',
                                  border: '1px solid #E2E8F0',
                                  color: '#334155',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: ACCENT_COLOR,
                                    color: '#fff',
                                    borderColor: ACCENT_COLOR,
                                    transform: 'scale(1.08)',
                                  },
                                }}
                              >
                                <ViewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>

                            {/* Edit Button — Hidden when Approved */}
                            {item.status?.toLowerCase() !== 'approved' && (
                              <Tooltip title={t('pages.customPush.tooltipEdit')}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenEdit(item)}
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    bgcolor: '#F1F5F9',
                                    border: '1px solid #E2E8F0',
                                    color: '#334155',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: '#334155',
                                      color: '#fff',
                                      transform: 'scale(1.08)',
                                    },
                                  }}
                                >
                                  <Edit sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {/* Delete Button — Hidden when Approved */}
                            {item.status?.toLowerCase() !== 'approved' && (
                              <Tooltip title={t('pages.customPush.tooltipDelete')}>
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteConfirmItem(item)}
                                  sx={{
                                    width: 34,
                                    height: 34,
                                    bgcolor: 'rgba(220, 53, 69, 0.08)',
                                    border: '1px solid rgba(220, 53, 69, 0.25)',
                                    color: '#DC2626',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: '#DC2626',
                                      color: '#fff',
                                      transform: 'scale(1.08)',
                                    },
                                  }}
                                >
                                  <Delete sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#64748B' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Campaign sx={{ fontSize: 36, color: ACCENT_COLOR, opacity: 0.7 }} />
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            {t('pages.customPush.noNotificationsTitle')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('pages.customPush.noNotificationsSubtitle')}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* ── CUSTOM (CATEGORY PUSH) NOTIFICATIONS VIEW ── */}
      {activeTab === 'custom' && (
        <>
          {/* Custom Notification History Table */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: '16px',
              border: '1px solid #E2E8F0',
              overflow: 'hidden',
              bgcolor: '#ffffff',
              boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)',
            }}
          >
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderDetails')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderCategory')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderTargetTitle')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderNote')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderDate')}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderStatus')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#0F172A', py: 2 }}>{t('pages.customPush.tableHeaderActions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} sx={{ color: ACCENT_COLOR }} />
                        <Typography variant="body2" sx={{ mt: 1.5, color: '#64748B', fontWeight: 500 }}>
                          {t('pages.customPush.loadingHistory')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredCustomHistory.length > 0 ? (
                    filteredCustomHistory.map((item) => {
                      const isPromo = item.module === 'Promotion';
                      const matchedEntity = isPromo
                        ? promotions.find((p) => String(p.id) === String(item.entityId))
                        : contacts.find((c) => String(c.id) === String(item.entityId));
                      const entityTitle = matchedEntity?.title || matchedEntity?.titleDe || matchedEntity?.name || (isPromo ? t('pages.customPush.categoryPromotion') : t('pages.customPush.categoryContact'));

                      return (
                        <TableRow key={item.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ py: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                              <Box
                                sx={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: '10px',
                                  bgcolor: 'rgba(255, 140, 66, 0.12)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  mt: 0.25,
                                }}
                              >
                                <Campaign sx={{ color: ACCENT_COLOR, fontSize: 20 }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0F172A', mb: 0.25 }}>
                                  {item.title}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#64748B',
                                    fontSize: '0.825rem',
                                    fontWeight: 400,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {item.body && item.body.length > 70 ? `${item.body.substring(0, 70)}...` : item.body}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            <Chip
                              label={isPromo ? t('pages.customPush.categoryPromotion') : t('pages.customPush.categoryContact')}
                              size="small"
                              sx={{
                                height: 24,
                                fontSize: '0.72rem',
                                fontWeight: 600,
                                borderRadius: '8px',
                                bgcolor: isPromo ? '#F3E8FF' : '#E0F2FE',
                                color: isPromo ? '#6B21A8' : '#0369A1',
                                border: isPromo ? '1px solid #E9D5FF' : '1px solid #BAE6FD',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 2, fontSize: '0.825rem', color: '#1E293B', fontWeight: 600, maxWidth: 200 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: '#1E293B',
                                fontSize: '0.825rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {entityTitle}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2, fontSize: '0.825rem', color: '#475569', maxWidth: 220 }}>
                            {item.adminNote ? (
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.4 }} title={item.adminNote}>
                                {item.adminNote}
                              </Typography>
                            ) : (
                              <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#94A3B8', fontStyle: 'italic' }}>
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 2, fontSize: '0.85rem', color: '#64748B', fontWeight: 500 }}>
                            {formatDate(item.createdAtUtc)}
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>
                            {(() => {
                              const statusLower = String(item.status || '').toLowerCase();
                              const isApproved = statusLower === 'approved' || statusLower === 'sent';
                              const isPending = statusLower === 'pending';
                              const isRejected = statusLower === 'rejected';

                              return (
                                <Chip
                                  label={item.status || 'Pending'}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    borderRadius: '8px',
                                    bgcolor: isApproved ? '#D1FAE5' : isPending ? '#FEF3C7' : isRejected ? '#FEE2E2' : '#EFF6FF',
                                    color: isApproved ? '#065F46' : isPending ? '#92400E' : isRejected ? '#991B1B' : '#1E40AF',
                                    border: isApproved ? '1px solid #A7F3D0' : isPending ? '1px solid #FDE68A' : isRejected ? '1px solid #FCA5A5' : '1px solid #BFDBFE',
                                  }}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 2 }}>
                            <Tooltip title={t('pages.customPush.tooltipView')}>
                              <IconButton
                                size="small"
                                onClick={() => setCustomViewItem(item)}
                                sx={{
                                  width: 34,
                                  height: 34,
                                  bgcolor: '#F1F5F9',
                                  border: '1px solid #E2E8F0',
                                  color: '#334155',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    bgcolor: ACCENT_COLOR,
                                    color: '#fff',
                                    borderColor: ACCENT_COLOR,
                                    transform: 'scale(1.08)',
                                  },
                                }}
                              >
                                <ViewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6, color: '#64748B' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1.5,
                          }}
                        >
                          <Campaign sx={{ fontSize: 36, color: ACCENT_COLOR, opacity: 0.7 }} />
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            {t('pages.customPush.historyEmptyTitle')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t('pages.customPush.historyEmptySubtitle')}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* TablePagination for Custom History */}
            <TablePagination
              component="div"
              count={customTotalRecords}
              page={Math.max(0, customPage - 1)}
              onPageChange={(e, newPage) => setCustomPage(newPage + 1)}
              rowsPerPage={customPageSize}
              onRowsPerPageChange={(e) => {
                setCustomPageSize(parseInt(e.target.value, 10));
                setCustomPage(1);
              }}
              rowsPerPageOptions={[10, 20, 50]}
              sx={{ borderTop: '1px solid #E2E8F0' }}
            />
          </Paper>
        </>
      )}


      {/* Modern High-End Add / Edit Notification Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(226, 232, 240, 0.8)',
            bgcolor: '#FFFFFF',
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                background: 'rgba(255, 140, 66, 0.15)',
                border: '1px solid rgba(255, 140, 66, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(255, 140, 66, 0.2)',
              }}
            >
              <NotificationsActiveIcon sx={{ color: ACCENT_COLOR, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#FFFFFF', lineHeight: 1.2 }}>
                {editingItem ? t('pages.customPush.dialogEditTitle') : t('pages.customPush.dialogAddTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '0.8rem', mt: 0.5, fontWeight: 400 }}>
                {editingItem ? t('pages.customPush.dialogEditSubtitle') : t('pages.customPush.dialogAddSubtitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setDialogOpen(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              p: 1,
              transition: 'all 0.2s ease',
              '&:hover': {
                color: '#FFFFFF',
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                transform: 'rotate(90deg)',
              },
            }}
          >
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 }, bgcolor: '#FFFFFF' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Notification Category Select Container */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                  {t('pages.customPush.fieldCategory')} <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
              </Box>
              <FormControl fullWidth size="small">
                <Select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setContactId('');
                    setPromotionId('');
                  }}
                  disabled={editingItem && editingItem.status === 'Approved'}
                  sx={{
                    borderRadius: '12px',
                    bgcolor: '#F8FAFC',
                    fontSize: '0.925rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                    '&.Mui-focused': {
                      bgcolor: '#FFFFFF',
                      boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                      '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                    },
                  }}
                >
                  <MenuItem value="Contact">{t('pages.customPush.categoryContact')}</MenuItem>
                  <MenuItem value="Promotion">{t('pages.customPush.categoryPromotion')}</MenuItem>
                  <MenuItem value="Custom">{t('pages.customPush.categoryCustom')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Contact Select Field Container */}
            {category === 'Contact' && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                    {t('pages.customPush.fieldContact')} <span style={{ color: '#EF4444' }}>*</span>
                  </Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={contactId}
                    onChange={(e) => setContactId(e.target.value)}
                    disabled={loadingContacts || (editingItem && editingItem.status === 'Approved')}
                    sx={{
                      borderRadius: '12px',
                      bgcolor: '#F8FAFC',
                      fontSize: '0.925rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#CBD5E1' },
                      '&.Mui-focused': {
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                        '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>{t('pages.customPush.fieldContactPlaceholder')}</em>
                    </MenuItem>
                    {contacts.map((c) => {
                      const label = c.title || c.titleDe || c.name || t('pages.customPush.categoryContact');
                      return (
                        <MenuItem key={c.id} value={c.id}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Promotion Select Field Container */}
            {category === 'Promotion' && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                    {t('pages.customPush.fieldPromotion')} <span style={{ color: '#EF4444' }}>*</span>
                  </Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={promotionId}
                    onChange={(e) => setPromotionId(e.target.value)}
                    disabled={loadingPromotions || (editingItem && editingItem.status === 'Approved')}
                    sx={{
                      borderRadius: '12px',
                      bgcolor: '#F8FAFC',
                      fontSize: '0.925rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#CBD5E1' },
                      '&.Mui-focused': {
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                        '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>{t('pages.customPush.fieldPromotionPlaceholder')}</em>
                    </MenuItem>
                    {promotions.map((p) => {
                      const label = p.title || p.name || t('pages.customPush.categoryPromotion');
                      return (
                        <MenuItem key={p.id} value={p.id}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Title Field Container */}
            <Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                  {t('pages.customPush.fieldTitle')} <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
              </Box>
              <TextField
                fullWidth
                placeholder={t('pages.customPush.fieldTitlePlaceholder')}
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                inputProps={{ maxLength: 100 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#F8FAFC',
                    fontSize: '0.925rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                    '&.Mui-focused': {
                      bgcolor: '#FFFFFF',
                      boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                      '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                    },
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: title.length >= 100 ? '#EF4444' : title.length > 80 ? '#F59E0B' : '#64748B',
                    bgcolor: title.length >= 100 ? '#FEE2E2' : '#F1F5F9',
                    px: 1,
                    py: 0.2,
                    borderRadius: '6px',
                  }}
                >
                  {title.length} / 100
                </Typography>
              </Box>
            </Box>

            {/* Message Body Field Container */}
            <Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                  {t('pages.customPush.fieldMessage')} <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
              </Box>
              <TextField
                fullWidth
                placeholder={t('pages.customPush.fieldMessagePlaceholder')}
                multiline
                rows={3.5}
                size="small"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                inputProps={{ maxLength: 300 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#F8FAFC',
                    fontSize: '0.925rem',
                    fontWeight: 500,
                    lineHeight: 1.5,
                    transition: 'all 0.2s ease',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                    '&.Mui-focused': {
                      bgcolor: '#FFFFFF',
                      boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                      '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                    },
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: body.length >= 300 ? '#EF4444' : body.length > 250 ? '#F59E0B' : '#64748B',
                    bgcolor: body.length >= 300 ? '#FEE2E2' : '#F1F5F9',
                    px: 1,
                    py: 0.2,
                    borderRadius: '6px',
                  }}
                >
                  {body.length} / 300
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        {/* Footer Actions */}
        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            py: 2.5,
            bgcolor: '#FFFFFF',
            borderTop: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: '10px',
              color: '#475569',
              bgcolor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              '&:hover': {
                bgcolor: '#F1F5F9',
                borderColor: '#CBD5E1',
              },
            }}
          >
            {t('pages.customPush.btnCancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveForm}
            disabled={
              submitting ||
              (category === 'Contact' && !contactId) ||
              (category === 'Promotion' && !promotionId) ||
              !title.trim() ||
              !body.trim()
            }
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: '18px !important' }} />}
            sx={{
              px: 3.5,
              py: 1,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #FF8C42 0%, #F76B1C 100%)',
              color: '#FFFFFF',
              boxShadow: '0 4px 14px rgba(255, 140, 66, 0.35)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #E06526 0%, #D85611 100%)',
                boxShadow: '0 6px 20px rgba(255, 140, 66, 0.45)',
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                background: '#E2E8F0',
                color: '#94A3B8',
                boxShadow: 'none',
              },
            }}
          >
            {submitting ? t('pages.customPush.btnSaving') : editingItem ? t('pages.customPush.btnUpdate') : t('pages.customPush.btnSend')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(deleteConfirmItem)}
        onClose={() => setDeleteConfirmItem(null)}
        PaperProps={{
          sx: { borderRadius: '16px', p: 1 },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#0F172A' }}>{t('pages.customPush.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748B' }}>
            {t('pages.customPush.deleteConfirmMessage', { title: deleteConfirmItem?.title })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteConfirmItem(null)} disabled={deleting} sx={{ color: '#64748B', textTransform: 'none', fontWeight: 600 }}>
            {t('pages.customPush.btnCancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} disabled={deleting} variant="contained" color="error" sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', minWidth: 90 }}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : t('pages.customPush.btnDelete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Notification Details Dialog */}
      <Dialog
        open={Boolean(viewItem)}
        onClose={() => setViewItem(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.35)',
            bgcolor: '#FFFFFF',
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                background: 'rgba(255, 140, 66, 0.15)',
                border: '1px solid rgba(255, 140, 66, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ViewIcon sx={{ color: ACCENT_COLOR, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#FFFFFF', lineHeight: 1.2 }}>
                {t('pages.customPush.viewDetailsTitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setViewItem(null)}
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '10px',
              p: 1,
              '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.15)' },
            }}
          >
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 }, bgcolor: '#FFFFFF' }}>
          {viewLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
              <CircularProgress size={32} sx={{ color: ACCENT_COLOR }} />
              <Typography variant="body2" sx={{ mt: 2, color: '#64748B' }}>
                {t('pages.customPush.viewDetailsLoading')}
              </Typography>
            </Box>
          ) : viewItem ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              {/* Notification Title & Status Chip on Right Side */}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('pages.customPush.viewFieldTitle')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', mt: 0.5, fontSize: '1.05rem', lineHeight: 1.3, wordBreak: 'break-word' }}>
                    {viewItem.title}
                  </Typography>
                </Box>
                <Box sx={{ flexShrink: 0, mt: 1 }}>
                  {viewItem?.status === 'Approved' && (
                    <Chip label={t('pages.customPush.statusApproved')} size="small" sx={{ height: 26, fontSize: '0.75rem', fontWeight: 700, borderRadius: '8px', bgcolor: '#D1FAE5', color: '#065F46', border: '1px solid #A7F3D0' }} />
                  )}
                  {viewItem?.status === 'Pending' && (
                    <Chip label={t('pages.customPush.statusPending')} size="small" sx={{ height: 26, fontSize: '0.75rem', fontWeight: 700, borderRadius: '8px', bgcolor: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A' }} />
                  )}
                  {viewItem?.status === 'Rejected' && (
                    <Chip label={t('pages.customPush.statusRejected')} size="small" sx={{ height: 26, fontSize: '0.75rem', fontWeight: 700, borderRadius: '8px', bgcolor: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5' }} />
                  )}
                </Box>
              </Box>

              {/* Message Content Container */}
              <Box sx={{ p: 2.25, bgcolor: '#F8FAFC', borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.75 }}>
                  {t('pages.customPush.viewFieldMessage')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontWeight: 400, fontSize: '0.9rem' }}>
                  {viewItem.body || viewItem.message}
                </Typography>
              </Box>

              {/* Admin Note if present */}
              {viewItem.note && (
                <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, display: 'block', mb: 0.5 }}>
                    {t('pages.customPush.viewFieldAdminNote')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.85rem', lineHeight: 1.4 }}>
                    {viewItem.note}
                  </Typography>
                </Box>
              )}

              {/* Metadata Cards Grid */}
              <Grid container spacing={2}>
                {viewItem.category && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                        {t('pages.customPush.viewFieldCategory')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.5, fontSize: '0.875rem' }}>
                        {t(`pages.customPush.category${viewItem.category}`) || viewItem.category}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {(() => {
                  const cat = viewItem.targetType || viewItem.category || 'Custom';
                  let targetTitleValue = viewItem.targetTitle;

                  if (!targetTitleValue) {
                    if (cat === 'Contact' || viewItem.contactId) {
                      const cId = viewItem.contactId || viewItem.targetEntityId;
                      const foundContact = contacts.find((c) => String(c.id) === String(cId));
                      targetTitleValue = foundContact?.title || foundContact?.titleDe || foundContact?.name || viewItem.contactTitle || (cId ? t('pages.customPush.categoryContact') : '');
                    } else if (cat === 'Promotion' || viewItem.promotionId) {
                      const pId = viewItem.promotionId || viewItem.targetEntityId;
                      const foundPromo = promotions.find((p) => String(p.id) === String(pId));
                      targetTitleValue = foundPromo?.title || foundPromo?.name || viewItem.promotionTitle || (pId ? t('pages.customPush.categoryPromotion') : '');
                    }
                  }

                  if (cat === 'Custom' && !targetTitleValue) {
                    targetTitleValue = '—';
                  }

                  if (!targetTitleValue) return null;

                  const cardLabel = cat === 'Contact' ? t('pages.customPush.viewFieldContact') : cat === 'Promotion' ? t('pages.customPush.viewFieldPromotion') : t('pages.customPush.viewFieldTargetTitle');

                  return (
                    <Grid item xs={12} sm={6}>
                      <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                          {cardLabel}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.5, fontSize: '0.875rem' }}>
                          {targetTitleValue}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })()}

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                      {t('pages.customPush.viewFieldDateCreated')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.5, fontSize: '0.875rem' }}>
                      {formatDate(viewItem.createdAt)}
                    </Typography>
                  </Box>
                </Grid>

                {viewItem.providerName && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                        {t('pages.customPush.viewFieldProviderName')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.5, fontSize: '0.875rem' }}>
                        {viewItem.providerName}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {viewItem.providerEmail && (
                  <Grid item xs={12} sm={12}>
                    <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                        {t('pages.customPush.viewFieldProviderEmail')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5, fontSize: '0.875rem' }}>
                        {viewItem.providerEmail}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, bgcolor: '#FFFFFF', borderTop: '1px solid #F1F5F9', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setViewItem(null)}
            variant="contained"
            sx={{
              bgcolor: '#0F172A',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 3.5,
              py: 1,
              fontSize: '0.875rem',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)',
              '&:hover': { bgcolor: '#1E293B', boxShadow: '0 6px 16px rgba(15, 23, 42, 0.3)' },
            }}
          >
            {t('pages.customPush.btnClose')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CUSTOM PUSH NOTIFICATION SEND DIALOG ── */}
      <Dialog
        open={customDialogOpen}
        onClose={() => setCustomDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(226, 232, 240, 0.8)',
            bgcolor: '#FFFFFF',
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                background: 'rgba(255, 140, 66, 0.15)',
                border: '1px solid rgba(255, 140, 66, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(255, 140, 66, 0.2)',
              }}
            >
              <Campaign sx={{ color: ACCENT_COLOR, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#FFFFFF', lineHeight: 1.2 }}>
                {t('pages.customPush.dialogCustomTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '0.8rem', mt: 0.5, fontWeight: 400 }}>
                {t('pages.customPush.dialogCustomSubtitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setCustomDialogOpen(false)}
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '10px',
              p: 1,
              transition: 'all 0.2s ease',
              '&:hover': {
                color: '#FFFFFF',
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                transform: 'rotate(90deg)',
              },
            }}
          >
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 }, bgcolor: '#FFFFFF' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {/* Category Field */}
            <Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                  {t('pages.customPush.fieldCategory')} <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
              </Box>
              <FormControl fullWidth size="small">
                <Select
                  value={customCategory}
                  onChange={(e) => {
                    setCustomCategory(e.target.value);
                    setCustomContactId('');
                    setCustomPromotionId('');
                  }}
                  sx={{
                    borderRadius: '12px',
                    bgcolor: '#F8FAFC',
                    fontSize: '0.925rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                    '&.Mui-focused': {
                      bgcolor: '#FFFFFF',
                      boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                      '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                    },
                  }}
                >
                  <MenuItem value="Contact">{t('pages.customPush.categoryContact')}</MenuItem>
                  <MenuItem value="Promotion">{t('pages.customPush.categoryPromotion')}</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Contact Select Field */}
            {customCategory === 'Contact' && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                    {t('pages.customPush.fieldContact')} <span style={{ color: '#EF4444' }}>*</span>
                  </Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={customContactId}
                    onChange={(e) => setCustomContactId(e.target.value)}
                    disabled={loadingContacts}
                    sx={{
                      borderRadius: '12px',
                      bgcolor: '#F8FAFC',
                      fontSize: '0.925rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#CBD5E1' },
                      '&.Mui-focused': {
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                        '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>{t('pages.customPush.fieldContactPlaceholder')}</em>
                    </MenuItem>
                    {contacts.map((c) => {
                      const label = c.title || c.titleDe || c.name || t('pages.customPush.categoryContact');
                      return (
                        <MenuItem key={c.id} value={c.id}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Promotion Select Field */}
            {customCategory === 'Promotion' && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                    {t('pages.customPush.fieldPromotion')} <span style={{ color: '#EF4444' }}>*</span>
                  </Typography>
                </Box>
                <FormControl fullWidth size="small">
                  <Select
                    displayEmpty
                    value={customPromotionId}
                    onChange={(e) => setCustomPromotionId(e.target.value)}
                    disabled={loadingPromotions}
                    sx={{
                      borderRadius: '12px',
                      bgcolor: '#F8FAFC',
                      fontSize: '0.925rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease',
                      '& fieldset': { borderColor: '#E2E8F0' },
                      '&:hover fieldset': { borderColor: '#CBD5E1' },
                      '&.Mui-focused': {
                        bgcolor: '#FFFFFF',
                        boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                        '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      <em>{t('pages.customPush.fieldPromotionPlaceholder')}</em>
                    </MenuItem>
                    {promotions.map((p) => {
                      const label = p.title || p.name || t('pages.customPush.categoryPromotion');
                      return (
                        <MenuItem key={p.id} value={p.id}>
                          {label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Notification Title */}
            <Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                  {t('pages.customPush.fieldTitle')} <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder={t('pages.customPush.fieldTitlePlaceholder')}
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                inputProps={{ maxLength: 100 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#F8FAFC',
                    fontSize: '0.925rem',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                    '&.Mui-focused': {
                      bgcolor: '#FFFFFF',
                      boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                      '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                    },
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: customTitle.length >= 100 ? '#EF4444' : customTitle.length > 80 ? '#F59E0B' : '#64748B',
                    bgcolor: customTitle.length >= 100 ? '#FEE2E2' : '#F1F5F9',
                    px: 1,
                    py: 0.2,
                    borderRadius: '6px',
                  }}
                >
                  {customTitle.length} / 100
                </Typography>
              </Box>
            </Box>

            {/* Notification Body / Message */}
            <Box>
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem' }}>
                  {t('pages.customPush.fieldMessage')} <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={3.5}
                size="small"
                placeholder={t('pages.customPush.fieldMessagePlaceholder')}
                value={customBody}
                onChange={(e) => setCustomBody(e.target.value)}
                inputProps={{ maxLength: 300 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: '#F8FAFC',
                    fontSize: '0.925rem',
                    fontWeight: 500,
                    lineHeight: 1.5,
                    transition: 'all 0.2s ease',
                    '& fieldset': { borderColor: '#E2E8F0' },
                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                    '&.Mui-focused': {
                      bgcolor: '#FFFFFF',
                      boxShadow: '0 0 0 3px rgba(255, 140, 66, 0.15)',
                      '& fieldset': { borderColor: ACCENT_COLOR, borderWidth: '1.5px' },
                    },
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.75 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: customBody.length >= 300 ? '#EF4444' : customBody.length > 250 ? '#F59E0B' : '#64748B',
                    bgcolor: customBody.length >= 300 ? '#FEE2E2' : '#F1F5F9',
                    px: 1,
                    py: 0.2,
                    borderRadius: '6px',
                  }}
                >
                  {customBody.length} / 300
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2.5, sm: 3.5 },
            py: 2.5,
            bgcolor: '#FFFFFF',
            borderTop: '1px solid #F1F5F9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1.5,
          }}
        >
          <Button
            onClick={() => setCustomDialogOpen(false)}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: '10px',
              color: '#475569',
              bgcolor: '#F8FAFC',
              border: '1px solid #E2E8F0',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              '&:hover': { bgcolor: '#F1F5F9', borderColor: '#CBD5E1' },
            }}
          >
            {t('pages.customPush.btnCancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSendCustomNotification}
            disabled={
              customSending ||
              (customCategory === 'Contact' && !customContactId) ||
              (customCategory === 'Promotion' && !customPromotionId) ||
              !customTitle.trim() ||
              !customBody.trim()
            }
            startIcon={customSending ? <CircularProgress size={18} color="inherit" /> : <SendIcon sx={{ fontSize: '18px !important' }} />}
            sx={{
              px: 3.5,
              py: 1,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #FF8C42 0%, #F76B1C 100%)',
              color: '#FFFFFF',
              boxShadow: '0 4px 14px rgba(255, 140, 66, 0.35)',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #E06526 0%, #D85611 100%)',
                boxShadow: '0 6px 20px rgba(255, 140, 66, 0.45)',
                transform: 'translateY(-1px)',
              },
              '&.Mui-disabled': {
                background: '#E2E8F0',
                color: '#94A3B8',
                boxShadow: 'none',
              },
            }}
          >
            {customSending ? t('pages.customPush.btnSaving') : t('pages.customPush.btnSend')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CUSTOM PUSH NOTIFICATION VIEW DETAILS DIALOG ── */}
      <Dialog
        open={Boolean(customViewItem)}
        onClose={() => setCustomViewItem(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.35)',
            bgcolor: '#FFFFFF',
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 3,
            background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                background: 'rgba(255, 140, 66, 0.15)',
                border: '1px solid rgba(255, 140, 66, 0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Campaign sx={{ color: ACCENT_COLOR, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#FFFFFF', lineHeight: 1.2 }}>
                {t('pages.customPush.viewDetailsTitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setCustomViewItem(null)}
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '10px',
              p: 1,
              '&:hover': { color: '#FFFFFF', bgcolor: 'rgba(255, 255, 255, 0.15)' },
            }}
          >
            <CloseRounded sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2.5, sm: 3.5 }, bgcolor: '#FFFFFF' }}>
          {customViewItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('pages.customPush.viewFieldTitle')}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F172A', mt: 0.5, fontSize: '1.05rem', lineHeight: 1.3, wordBreak: 'break-word' }}>
                    {customViewItem.title}
                  </Typography>
                </Box>
                <Box sx={{ flexShrink: 0, mt: 1 }}>
                  <Chip
                    label={customViewItem.status || 'Pending'}
                    size="small"
                    sx={{
                      height: 26,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      borderRadius: '8px',
                      bgcolor: customViewItem.status === 'Approved' ? '#D1FAE5' : customViewItem.status === 'Pending' ? '#FEF3C7' : '#FEE2E2',
                      color: customViewItem.status === 'Approved' ? '#065F46' : customViewItem.status === 'Pending' ? '#92400E' : '#991B1B',
                      border: '1px solid rgba(0,0,0,0.08)',
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ p: 2.25, bgcolor: '#F8FAFC', borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 0.75 }}>
                  {t('pages.customPush.viewFieldMessage')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontWeight: 400, fontSize: '0.9rem' }}>
                  {customViewItem.body}
                </Typography>
              </Box>

              {customViewItem.adminNote && (
                <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: '12px', border: '1px solid #E2E8F0' }}>
                  <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, display: 'block', mb: 0.5 }}>
                    {t('pages.customPush.viewFieldAdminNote')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.85rem', lineHeight: 1.4 }}>
                    {customViewItem.adminNote}
                  </Typography>
                </Box>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                      {t('pages.customPush.viewFieldModule')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.5, fontSize: '0.875rem' }}>
                      {customViewItem.module === 'Promotion' ? t('pages.customPush.categoryPromotion') : t('pages.customPush.categoryContact')}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                      {customViewItem.module === 'Promotion' ? t('pages.customPush.viewFieldPromotion') : t('pages.customPush.viewFieldContact')}
                    </Typography>
                    {(() => {
                      const isPromo = customViewItem.module === 'Promotion';
                      const found = isPromo
                        ? promotions.find((p) => String(p.id) === String(customViewItem.entityId))
                        : contacts.find((c) => String(c.id) === String(customViewItem.entityId));
                      const targetName = found?.title || found?.titleDe || found?.name || (isPromo ? t('pages.customPush.categoryPromotion') : t('pages.customPush.categoryContact'));
                      return (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.5, fontSize: '0.875rem' }}>
                          {targetName}
                        </Typography>
                      );
                    })()}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                      {t('pages.customPush.viewFieldDateCreated')}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.5, fontSize: '0.875rem' }}>
                      {formatDate(customViewItem.createdAtUtc)}
                    </Typography>
                  </Box>
                </Grid>

                {customViewItem.senderName && (
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                        {t('pages.customPush.viewFieldProviderName')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1E293B', mt: 0.5, fontSize: '0.875rem' }}>
                        {customViewItem.senderName} ({customViewItem.senderRole || 'Provider'})
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {customViewItem.senderEmail && (
                  <Grid item xs={12} sm={customViewItem.senderName ? 6 : 12}>
                    <Box sx={{ p: 1.75, borderRadius: '12px', bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, fontSize: '0.75rem', display: 'block' }}>
                        {t('pages.customPush.viewFieldProviderEmail')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', mt: 0.5, fontSize: '0.875rem' }}>
                        {customViewItem.senderEmail}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, bgcolor: '#FFFFFF', borderTop: '1px solid #F1F5F9', justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setCustomViewItem(null)}
            variant="contained"
            sx={{
              bgcolor: '#0F172A',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '10px',
              px: 3.5,
              py: 1,
              fontSize: '0.875rem',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.2)',
              '&:hover': { bgcolor: '#1E293B', boxShadow: '0 6px 16px rgba(15, 23, 42, 0.3)' },
            }}
          >
            {t('pages.customPush.btnClose')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

